import {Component, computed, inject, Injector, output, signal, ChangeDetectionStrategy} from "@angular/core"
import {isInstanceOf} from "@cm/utils"
import {MatDialog} from "@angular/material/dialog"
import {quaternionToAngleDegrees} from "@app/common/helpers/utils/math-utils"
import {UploadGqlService} from "@app/common/services/upload/upload.gql.service"
import {getNodeIconClass, getNodeIconSecondaryClass} from "@app/template-editor/helpers/template-icons"
import {MeshConversionError, MeshMissingUvsError, selectFile, uploadImage, uploadMeshToGroup} from "@app/template-editor/helpers/upload"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {TemplateNodeDragService} from "@app/template-editor/services/template-node-drag.service"
import {WebAssemblyWorkerService} from "@app/template-editor/services/webassembly-worker.service"
import {Matrix4, Vector3} from "@cm/math"
import {
    Annotation,
    BooleanValue,
    CentroidAccumulator,
    ConfigGroup,
    CurveGeometryAttributeSet,
    CurveGeometryBoundaryOperator,
    CurveGeometryCastOperator,
    CurveGeometryConstOperator,
    CurveGeometryDiffOperator,
    CurveGeometryFilterOperator,
    CurveGeometryFlattenOperator,
    CurveGeometryFlipOperator,
    CurveGeometryGetAttribute,
    CurveGeometryGetAttributeComponent,
    CurveGeometryGetAttributeSet,
    CurveGeometryJoinOperator,
    CurveGeometryLabelPrimitivesOperator,
    CurveGeometryMathOperator,
    CurveGeometryMergeOperator,
    CurveGeometryPackOperator,
    CurveGeometryPartitionOperator,
    CurveGeometryPrimitive,
    CurveGeometryProductOperator,
    CurveGeometryProductOperatorPointGeometryCurveGeometryInput,
    CurveGeometryProjectOperator,
    CurveGeometrySplitOperator,
    CurveGeometryWeldOperator,
    CutMesh,
    DimensionGuideBox,
    DimensionGuides,
    FixedRotation,
    GeometryGraphCurve,
    GeometryGraphMesh,
    GeometryGraphPoint,
    GeometryGraphPointScalar,
    GeometryReduceOperator,
    getNodeOwner,
    Group,
    ImageRgba,
    ImageRgbCurve,
    JsonValue,
    MoveImage,
    MaterialAssignments,
    MeshDecal,
    MeshGeometryAttributeSet,
    MeshGeometryCastOperator,
    MeshGeometryConstOperator,
    MeshGeometryCutOperator,
    MeshGeometryFilterOperator,
    MeshGeometryFlipOperator,
    MeshGeometryGetAttribute,
    MeshGeometryGetAttributeComponent,
    MeshGeometryGetAttributeSet,
    MeshGeometryJoinOperator,
    MeshGeometryLabelPrimitivesOperator,
    MeshGeometryMathOperator,
    MeshGeometryMergeOperator,
    MeshGeometryNormalsOperator,
    MeshGeometryPackOperator,
    MeshGeometryPartitionOperator,
    MeshGeometryPrimitive,
    MeshGeometryProductOperator,
    MeshGeometryProductOperatorPointGeometryMeshGeometryInput,
    MeshGeometryProjectOperator,
    MeshGeometrySplitOperator,
    MeshGeometrySubdivideOperator,
    MeshGeometryTriangulateOperator,
    MeshGeometryWeldOperator,
    TemplateNode,
    TemplateNodes,
    NumberValue,
    ObjectNode,
    OverlayMaterialColor,
    PdfSettings,
    PlanarCurve,
    PlaneGuide,
    PointGeometryAttributeSet,
    PointGeometryBoundaryOperator,
    PointGeometryCastOperator,
    PointGeometryConstOperator,
    PointGeometryFilterOperator,
    PointGeometryGetAttribute,
    PointGeometryGetAttributeComponent,
    PointGeometryGetAttributeSet,
    PointGeometryJoinOperator,
    PointGeometryLabelPrimitivesOperator,
    PointGeometryMathOperator,
    PointGeometryMergeOperator,
    PointGeometryPackOperator,
    PointGeometryPartitionOperator,
    PointGeometryPrimitive,
    PointGeometryProductOperator,
    PointGeometryProductOperatorPointGeometryPointGeometryInput,
    PointGeometryProjectOperator,
    PointGeometrySplitOperator,
    PointGeometryWeldOperator,
    PointGuide,
    ProceduralMesh,
    ProfileExtrudeMesh,
    RigidRelation,
    SceneNodes,
    StoredMesh,
    StringValue,
    TransformMatrix,
    Translation,
    fundamentalTypes,
    BackgroundMaterial,
    BlendMaterialSlot,
    DataObjectReference,
    DistanceTexture,
    ExtractMaterialSlot,
    FontMaterial,
    createCurveGeometryAttributeSetFromSet,
    createMeshGeometryAttributeSetFromSet,
    createPointGeometryAttributeSetFromSet,
    createCurveGeometryUnaryOrBinaryMathOperator,
    createMeshGeometryUnaryOrBinaryMathOperator,
    createPointGeometryUnaryOrBinaryMathOperator,
    createCurveGeometryPrimitive,
    createMeshGeometryPrimitive,
    createPointGeometryPrimitive,
    ImageDisplacementToNormal,
    ImageDistanceTransform,
    ImageOperator,
    MathOperator,
    NodesInput,
    LodType,
    MeshCurve,
    MoveMaterial,
    Seam,
    TransformMaterialSlotType,
    initDimensionGuides,
    TransformToValue,
} from "@cm/template-nodes"
import {createMattress, Mattress} from "@cm/mattress-nodes"
import {v4 as uuid4} from "uuid"
import {SearchComponent} from "@app/common/components/inputs/search/search.component"
import {TemplateAddCardComponent} from "@app/template-editor/components/template-add-card/template-add-card.component"
import {TemplateAddOtherMeshSpecificMaterial} from "@app/template-editor/components/template-add-other-mesh-specific-material/template-add-other-mesh-specific-material.component"
import {TemplateAddOtherSystemTemplate} from "@app/template-editor/components/template-add-other-system-template/template-add-other-system-template.component"
import {TemplateAddOtherMaterialRange} from "@app/template-editor/components/template-add-other-material-range/template-add-other-material-range.component"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {defer, from} from "rxjs"

type SceneItem = {
    name: string
    node?: TemplateNode
    action?: () => Promise<false | void> | false | void
    iconClass: string
    secondaryIconClass?: string | null
    disabled?: boolean
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-add-other",
    templateUrl: "./template-add-other.component.html",
    styleUrl: "./template-add-other.component.scss",
    imports: [TemplateAddCardComponent, TemplateAddOtherSystemTemplate, SearchComponent, TemplateAddOtherMeshSpecificMaterial, TemplateAddOtherMaterialRange],
})
export class TemplateAddOtherComponent {
    readonly drag = inject(TemplateNodeDragService)
    private readonly sceneManagerService = inject(SceneManagerService)
    private readonly injector = inject(Injector)
    private readonly uploadService = inject(UploadGqlService)
    private readonly workerService = inject(WebAssemblyWorkerService)
    private readonly dialog = inject(MatDialog)
    private readonly notifications = inject(NotificationsService)
    readonly onItemClicked = output()

    readonly $currentSection = signal<
        | "upload"
        | "configuration"
        | "primitive"
        | "decal"
        | "input"
        | "export"
        | "value"
        | "connection"
        | "geometry"
        | "other"
        | "systemTemplate"
        | "meshSpecificMaterial"
        | "materialRange"
    >("upload")

    private readonly $selectedNodes = computed(() => this.sceneManagerService.$selectedRootNodeParts().map((node) => node.templateNode))
    private readonly $selectedObjects = computed(() => this.$selectedNodes().filter(isInstanceOf(ObjectNode)))
    private readonly $selectedPointGuideNodes = computed(() =>
        this.sceneManagerService
            .$selectedRootNodeParts()
            .map((templateNodePart) => this.sceneManagerService.getSceneNodes(templateNodePart.templateNode))
            .flat()
            .filter((x): x is SceneNodes.Mesh => SceneNodes.Mesh.is(x)),
    )

    readonly $systemTemplatesSearch = signal<string>("")
    readonly $meshSpecificMaterialsSearch = signal<string>("")
    readonly $materialRangesSearch = signal<string>("")

    readonly $uploadSceneItems = computed<SceneItem[]>(() => {
        return [
            {
                name: "Upload Mesh",
                action: () => this.uploadMesh(),
                iconClass: getNodeIconClass(StoredMesh.getNodeClass()),
            },
            {
                name: "Upload Image",
                action: () => this.uploadImage(),
                iconClass: getNodeIconClass(DataObjectReference.getNodeClass()),
            },
        ]
    })

    readonly $configurationSceneItems = computed<SceneItem[]>(() => {
        return [
            {
                name: "Config Group",
                node: new ConfigGroup({
                    parameters: {name: "Config Group", nodes: new TemplateNodes({parameters: {list: []}}), id: uuid4(), displayWithLabels: false},
                }),
                iconClass: getNodeIconClass(ConfigGroup.getNodeClass()),
            },
            {
                name: "Group",
                node: new Group({parameters: {name: "Group", nodes: new TemplateNodes({parameters: {list: []}}), active: true}}),
                iconClass: getNodeIconClass(Group.getNodeClass()),
            },
        ]
    })

    readonly $primitiveSceneItems = computed<SceneItem[]>(() => {
        return [
            {
                name: "Plane",
                node: new ProceduralMesh({
                    parameters: {
                        name: "Plane",
                        geometryGraph: "plane",
                        parameters: {width: 1000, height: 1000},
                        materialAssignments: new MaterialAssignments({parameters: {"0": null}}),
                        materialSlotNames: {},
                        lockedTransform: Matrix4.identity().toArray(),
                        visible: true,
                        visibleDirectly: true,
                        visibleInReflections: true,
                        visibleInRefractions: true,
                        castRealtimeShadows: true,
                        receiveRealtimeShadows: true,
                    },
                }),
                iconClass: getNodeIconClass(ProceduralMesh.getNodeClass()),
            },
            {
                name: "Box",
                node: new ProceduralMesh({
                    parameters: {
                        name: "Box",
                        geometryGraph: "box",
                        parameters: {width: 100, height: 100, depth: 100, inside: false, faceMaterials: false},
                        materialAssignments: new MaterialAssignments({parameters: {"0": null, "1": null, "2": null}}),
                        materialSlotNames: {"0": "Bottom", "1": "Outside", "2": "Top"},
                        lockedTransform: Matrix4.identity().toArray(),
                        visible: true,
                        visibleDirectly: true,
                        visibleInReflections: true,
                        visibleInRefractions: true,
                        castRealtimeShadows: true,
                        receiveRealtimeShadows: true,
                    },
                }),
                iconClass: getNodeIconClass(ProceduralMesh.getNodeClass()),
            },
            {
                name: "Sphere",
                node: new ProceduralMesh({
                    parameters: {
                        name: "Sphere",
                        geometryGraph: "sphere",
                        parameters: {radius: 10, numU: 64, numV: 32},
                        materialAssignments: new MaterialAssignments({parameters: {"0": null}}),
                        materialSlotNames: {},
                        lockedTransform: Matrix4.identity().toArray(),
                        visible: true,
                        visibleDirectly: true,
                        visibleInReflections: true,
                        visibleInRefractions: true,
                        castRealtimeShadows: true,
                        receiveRealtimeShadows: true,
                    },
                }),
                iconClass: getNodeIconClass(ProceduralMesh.getNodeClass()),
            },
            {
                name: "Studio",
                node: new ProceduralMesh({
                    parameters: {
                        name: "Studio",
                        geometryGraph: "simpleStudioRoom",
                        parameters: {height: 5, length: 10, width: 10, showCeiling: true, showFloor: true, showWalls: true},
                        materialAssignments: new MaterialAssignments({parameters: {"0": null, "1": null, "2": null}}),
                        materialSlotNames: {"0": "Floor", "1": "Walls", "2": "Ceiling"},
                        lockedTransform: Matrix4.identity().toArray(),
                        visible: true,
                        visibleDirectly: true,
                        visibleInReflections: true,
                        visibleInRefractions: true,
                        castRealtimeShadows: true,
                        receiveRealtimeShadows: true,
                    },
                }),
                iconClass: getNodeIconClass(ProceduralMesh.getNodeClass()),
            },
        ]
    })

    readonly $decalSceneItems = computed<SceneItem[]>(() => {
        return [
            {
                name: "Mesh Decal",
                node: new MeshDecal({
                    parameters: {
                        name: "Mesh Decal",
                        mesh: null,
                        offset: [0, 0],
                        rotation: 0,
                        size: [10, 10],
                        distance: 0.01,
                        mask: undefined,
                        invertMask: false,
                        maskType: "binary",
                        materialAssignment: null,
                        visible: true,
                    },
                }),
                iconClass: getNodeIconClass(MeshDecal.getNodeClass()),
            },
            {
                name: "Mesh Curve",
                node: new MeshCurve({
                    parameters: {
                        name: "Mesh Curve",
                        mesh: null,
                        closed: false,
                        controlPoints: [],
                        visible: true,
                    },
                }),
                iconClass: getNodeIconClass(MeshCurve.getNodeClass()),
            },
            {
                name: "Planar Curve",
                node: new PlanarCurve({
                    parameters: {
                        name: "Planar Curve",
                        closed: false,
                        controlPoints: [],
                        visible: true,
                        resolution: 0.1,
                    },
                }),
                iconClass: getNodeIconClass(PlanarCurve.getNodeClass()),
            },
            {
                name: "Seam",
                node: new Seam({
                    parameters: {
                        name: "Seam",
                        item: null,
                        curves: [],
                        allowScaling: false,
                        visible: true,
                    },
                }),
                iconClass: getNodeIconClass(Seam.getNodeClass()),
            },
            {
                name: "Cut Mesh",
                node: new CutMesh({
                    parameters: {
                        name: "Cut Mesh",
                        mesh: null,
                        curves: [],
                        uvChannel: 0,
                        materialAssignments: new MaterialAssignments({parameters: {"0": null}}),
                        materialSlotNames: {},
                        lockedTransform: Matrix4.identity().toArray(),
                        visible: true,
                        visibleDirectly: true,
                        visibleInReflections: true,
                        visibleInRefractions: true,
                        castRealtimeShadows: true,
                        receiveRealtimeShadows: true,
                    },
                }),
                iconClass: getNodeIconClass(CutMesh.getNodeClass()),
            },
            {
                name: "Annotation",
                node: new Annotation({
                    parameters: {
                        name: "Annotation",
                        id: uuid4(),
                        label: "Label",
                        description: "",
                        lockedTransform: Matrix4.identity().toArray(),
                        visible: true,
                    },
                }),
                iconClass: getNodeIconClass(Annotation.getNodeClass()),
            },
            {
                name: "Dimension Guide",
                node: initDimensionGuides("Dimension Guide", null),
                iconClass: getNodeIconClass(DimensionGuides.getNodeClass()),
            },
            {
                name: "Dimension Guide Box",
                node: new DimensionGuideBox({
                    parameters: {
                        name: "Dimension Guide Box",
                        lockedTransform: Matrix4.identity().toArray(),
                        width: 100,
                        height: 100,
                        depth: 100,
                        visible: true,
                        parent: null,
                    },
                }),
                iconClass: getNodeIconClass(DimensionGuideBox.getNodeClass()),
            },
            {
                name: "Profile Extrude Mesh",
                node: new ProfileExtrudeMesh({
                    parameters: {
                        name: "Profile Extrude Mesh",
                        baseCurve: null,
                        profileCurve: null,
                        materialAssignments: new MaterialAssignments({parameters: {"0": null}}),
                        materialSlotNames: {},
                        lockedTransform: Matrix4.identity().toArray(),
                        visible: true,
                        visibleDirectly: true,
                        visibleInReflections: true,
                        visibleInRefractions: true,
                        castRealtimeShadows: true,
                        receiveRealtimeShadows: true,
                    },
                }),
                iconClass: getNodeIconClass(ProfileExtrudeMesh.getNodeClass()),
            },
        ]
    })

    readonly $inputSceneItems = computed<SceneItem[]>(() => {
        return [
            ...Object.values(fundamentalTypes).map((ft) => ({
                name: `${ft.name} Input`,
                node: new ft.Input({parameters: {name: `${ft.name} Input`, id: uuid4()}}),
                iconClass: getNodeIconClass(ft.Input.getNodeClass()),
            })),
            {
                name: "Nodes Input",
                node: new NodesInput({parameters: {name: "Nodes Input", id: uuid4()}}),
                iconClass: getNodeIconClass(NodesInput.getNodeClass()),
            },
        ]
    })

    readonly $exportSceneItems = computed<SceneItem[]>(() => {
        return Object.values(fundamentalTypes).map((ft) => ({
            name: `${ft.name} Output`,
            node: new ft.Export({parameters: {name: `${ft.name} Output`, id: uuid4(), node: null}}),
            iconClass: getNodeIconClass(ft.Export.getNodeClass()),
        }))
    })

    readonly $valueSceneItems = computed<SceneItem[]>(() => {
        return [
            {
                name: "String Value",
                node: new StringValue({
                    parameters: {
                        value: "",
                    },
                }),
                iconClass: getNodeIconClass(StringValue.getNodeClass()),
            },
            {
                name: "Number Value",
                node: new NumberValue({
                    parameters: {
                        value: 0,
                    },
                }),
                iconClass: getNodeIconClass(NumberValue.getNodeClass()),
            },
            {
                name: "Boolean Value",
                node: new BooleanValue({
                    parameters: {
                        value: false,
                    },
                }),
                iconClass: getNodeIconClass(BooleanValue.getNodeClass()),
            },
            {
                name: "JSON Value",
                node: new JsonValue({
                    parameters: {
                        value: {},
                    },
                }),
                iconClass: getNodeIconClass(JsonValue.getNodeClass()),
            },
            {
                name: "Transform Matrix",
                node: new TransformMatrix({
                    parameters: {
                        name: "Transform Matrix",
                        transform: {
                            type: "TranslationRotationScale",
                            translation: [0, 0, 0],
                            rotation: [0, 0, 0],
                            scale: [1, 1, 1],
                        },
                    },
                }),
                iconClass: getNodeIconClass(TransformMatrix.getNodeClass()),
            },
        ]
    })

    readonly $connectionSceneItems = computed<SceneItem[]>(() => {
        return [
            {
                name: "Rigid Relation",
                node: new RigidRelation({
                    parameters: {
                        translation: new Translation({parameters: {x: 0, y: 0, z: 0}}),
                        rotation: new FixedRotation({parameters: {x: 0, y: 0, z: 0}}),
                        targetA: null,
                        targetB: null,
                    },
                }),
                iconClass: getNodeIconClass(RigidRelation.getNodeClass()),
            },
            {
                name: "Rigidly Connect Selection",
                action: () => this.rigidlyConnectSelection(),
                iconClass: "far fa-arrow-right-arrow-left",
                disabled: this.$selectedObjects().length <= 1,
            },
            {
                name: "Point Guide from Selection",
                action: () => this.addPointGuideForSelection(),
                iconClass: getNodeIconClass(PointGuide.getNodeClass()),
                disabled: this.$selectedPointGuideNodes().length === 0,
            },
            {
                name: "Floor Guide",
                node: new PlaneGuide({
                    parameters: {
                        name: "Floor Guide",
                        width: 100,
                        height: 100,
                        lockedTransform: Matrix4.identity().toArray(),
                        visible: true,
                    },
                }),
                iconClass: getNodeIconClass(PlaneGuide.getNodeClass()),
            },
            {
                name: "Wall Guide",
                node: new PlaneGuide({
                    parameters: {
                        name: "Wall Guide",
                        width: 100,
                        height: 100,
                        lockedTransform: Matrix4.rotationX(90).toArray(),
                        visible: true,
                    },
                }),
                iconClass: getNodeIconClass(PlaneGuide.getNodeClass()),
            },
        ]
    })

    readonly $otherSceneItems = computed<SceneItem[]>(() => {
        return [
            {
                name: "Overlay Material Color",
                node: new OverlayMaterialColor({
                    parameters: {
                        name: "Overlay Material Color",
                        material: null,
                        overlay: null,
                        size: [10, 10],
                    },
                }),
                iconClass: getNodeIconClass(OverlayMaterialColor.getNodeClass()),
            },
            {
                name: "Blend Material Slot",
                node: new BlendMaterialSlot({
                    parameters: {
                        name: "Blend Material Slot",
                        material: null,
                        image: null,
                        slot: TransformMaterialSlotType.BaseColor,
                        blendAmount: 1,
                        uvChannel: 0,
                        size: [10, 10],
                    },
                }),
                iconClass: getNodeIconClass(BlendMaterialSlot.getNodeClass()),
            },
            {
                name: "Font Material",
                node: new FontMaterial({
                    parameters: {
                        name: "Font Material",
                        glyphImage: null,
                        glyphsConfiguration: null,
                        glyphHeightCm: 1,
                        text: null,
                        alignment: "center",
                        stamped: false,
                    },
                }),
                iconClass: getNodeIconClass(FontMaterial.getNodeClass()),
            },
            {
                name: "Extract Material Slot",
                node: new ExtractMaterialSlot({
                    parameters: {
                        name: "Extract Material Slot",
                        material: null,
                        slot: TransformMaterialSlotType.BaseColor,
                    },
                }),
                iconClass: getNodeIconClass(ExtractMaterialSlot.getNodeClass()),
            },
            {
                name: "Move Material",
                node: new MoveMaterial({
                    parameters: {
                        name: "Move Material",
                        material: null,
                    },
                }),
                iconClass: getNodeIconClass(MoveMaterial.getNodeClass()),
            },
            {
                name: "Background Material",
                node: new BackgroundMaterial({
                    parameters: {
                        backgroundImage: null,
                    },
                }),
                iconClass: getNodeIconClass(BackgroundMaterial.getNodeClass()),
            },
            {
                name: "LOD Type",
                node: new LodType({
                    parameters: {
                        lodType: "web",
                    },
                }),
                iconClass: getNodeIconClass(LodType.getNodeClass()),
            },
            {
                name: "Math Operator",
                node: new MathOperator({
                    parameters: {
                        name: "Math Operator",
                        type: "add",
                        inputA: null,
                        inputB: null,
                    },
                }),
                iconClass: getNodeIconClass(MathOperator.getNodeClass()),
            },
            {
                name: "Tranform To Value",
                node: new TransformToValue({
                    parameters: {
                        name: "Transform To Value",
                        transform: null,
                        valueType: "positionX",
                    },
                }),
                iconClass: getNodeIconClass(TransformToValue.getNodeClass()),
            },
            {
                name: "Image Operator",
                node: new ImageOperator({
                    parameters: {
                        name: "Image Operator",
                        input: null,
                        operation: "ADD",
                        value: 0,
                    },
                }),
                iconClass: getNodeIconClass(ImageOperator.getNodeClass()),
            },
            {
                name: "Image RGB Curve",
                node: new ImageRgbCurve({
                    parameters: {
                        name: "Image RGB Curve",
                        input: null,
                        r: [
                            {x: 0, y: 0},
                            {x: 1, y: 1},
                        ],
                        g: [
                            {x: 0, y: 0},
                            {x: 1, y: 1},
                        ],
                        b: [
                            {x: 0, y: 0},
                            {x: 1, y: 1},
                        ],
                        rgb: [
                            {x: 0, y: 0},
                            {x: 1, y: 1},
                        ],
                    },
                }),
                iconClass: getNodeIconClass(ImageOperator.getNodeClass()),
            },
            {
                name: "Image RGBA",
                node: new ImageRgba({
                    parameters: {
                        name: "Image RGBA",
                        color: [1, 1, 1],
                        alpha: 1,
                    },
                }),
                iconClass: getNodeIconClass(ImageRgba.getNodeClass()),
            },
            {
                name: "Move Image",
                node: new MoveImage({
                    parameters: {
                        name: "Move Image",
                        image: null,
                    },
                }),
                iconClass: getNodeIconClass(MoveImage.getNodeClass()),
            },
            {
                name: "Distance Texture",
                node: new DistanceTexture({
                    parameters: {
                        name: "Distance Texture",
                        source: null,
                        uvChannel: 0,
                        range: 1,
                        mergeRange: 0,
                        width: 1024,
                        height: 1024,
                        targets: [],
                        smoothPasses: 0,
                    },
                }),
                iconClass: getNodeIconClass(DistanceTexture.getNodeClass()),
            },
            {
                name: "Image Distance Transform",
                node: new ImageDistanceTransform({
                    parameters: {
                        name: "Image Distance Transform",
                        source: null,
                        threshold: 0.5,
                        resolution: 10,
                        width: 10,
                        height: 10,
                        refinementIterations: 2,
                    },
                }),
                iconClass: getNodeIconClass(ImageDistanceTransform.getNodeClass()),
            },
            {
                name: "Image Displacement to Normal",
                node: new ImageDisplacementToNormal({
                    parameters: {
                        name: "Image Displacement to Normal",
                        displacement: null,
                        displacementMax: 1,
                        displacementMin: 0,
                        displacementNormalSmoothness: 1,
                        displacementNormalStrength: 1,
                        resolution: 10,
                        width: 10,
                        height: 10,
                    },
                }),
                iconClass: getNodeIconClass(ImageDisplacementToNormal.getNodeClass()),
            },
            {
                name: "Mattress",
                node: createMattress(),
                iconClass: getNodeIconClass(Mattress.getNodeClass()),
            },
            {
                name: "PDF Settings",
                node: new PdfSettings({
                    parameters: {
                        name: "PDF Settings",
                        title: "Your Configuration",
                    },
                }),
                iconClass: getNodeIconClass(PdfSettings.getNodeClass()),
            },
        ]
    })

    readonly $geometrySceneItems = computed<SceneItem[]>(() => {
        return [
            {
                name: "Geometry Graph Point",
                node: new GeometryGraphPoint({
                    parameters: {
                        name: "Geometry Graph Point",
                        pointAttributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(GeometryGraphPoint.getNodeClass()),
            },
            {
                name: "Geometry Graph Point Scalar",
                node: new GeometryGraphPointScalar({
                    parameters: {
                        name: "Geometry Graph Point Scalar",
                        geometryGraphPoint: null,
                        scalarPath: null,
                    },
                }),
                iconClass: getNodeIconClass(GeometryGraphPointScalar.getNodeClass()),
            },
            {
                name: "Geometry Graph Curve",
                node: new GeometryGraphCurve({
                    parameters: {
                        name: "Geometry Graph Curve",
                        curveAttributeSet: null,
                        visible: true,
                    },
                }),
                iconClass: getNodeIconClass(GeometryGraphCurve.getNodeClass()),
            },
            {
                name: "Geometry Graph Mesh",
                node: new GeometryGraphMesh({
                    parameters: {
                        name: "Geometry Graph Mesh",
                        meshAttributeSet: null,
                        materialAssignments: new MaterialAssignments({parameters: {"0": null}}),
                        materialSlotNames: {},
                        lockedTransform: Matrix4.identity().toArray(),
                        visible: true,
                        visibleDirectly: true,
                        visibleInReflections: true,
                        visibleInRefractions: true,
                        castRealtimeShadows: true,
                        receiveRealtimeShadows: true,
                    },
                }),
                iconClass: getNodeIconClass(GeometryGraphMesh.getNodeClass()),
            },
            {
                name: "Attribute Set",
                node: createPointGeometryAttributeSetFromSet("Attribute Set", {}),
                iconClass: getNodeIconClass(PointGeometryAttributeSet.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryAttributeSet.getNodeClass()),
            },
            {
                name: "Attribute Set",
                node: createCurveGeometryAttributeSetFromSet("Attribute Set", {}),
                iconClass: getNodeIconClass(CurveGeometryAttributeSet.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryAttributeSet.getNodeClass()),
            },
            {
                name: "Attribute Set",
                node: createMeshGeometryAttributeSetFromSet("Attribute Set", {}),
                iconClass: getNodeIconClass(MeshGeometryAttributeSet.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryAttributeSet.getNodeClass()),
            },
            {
                name: "Get Attribute",
                node: new PointGeometryGetAttribute({
                    parameters: {
                        name: "Get Attribute",
                        attributeName: null,
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryGetAttribute.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryGetAttribute.getNodeClass()),
            },
            {
                name: "Get Attribute",
                node: new CurveGeometryGetAttribute({
                    parameters: {
                        name: "Get Attribute",
                        attributeName: null,
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryGetAttribute.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryGetAttribute.getNodeClass()),
            },
            {
                name: "Get Attribute",
                node: new MeshGeometryGetAttribute({
                    parameters: {
                        name: "Get Attribute",
                        attributeName: null,
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryGetAttribute.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryGetAttribute.getNodeClass()),
            },
            {
                name: "Get Attribute Set",
                node: new PointGeometryGetAttributeSet({
                    parameters: {
                        name: "Get Attribute Set",
                        attributeSetName: null,
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryGetAttributeSet.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryGetAttributeSet.getNodeClass()),
            },
            {
                name: "Get Attribute Set",
                node: new CurveGeometryGetAttributeSet({
                    parameters: {
                        name: "Get Attribute Set",
                        attributeSetName: null,
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryGetAttributeSet.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryGetAttributeSet.getNodeClass()),
            },
            {
                name: "Get Attribute Set",
                node: new MeshGeometryGetAttributeSet({
                    parameters: {
                        name: "Get Attribute Set",
                        attributeSetName: null,
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryGetAttributeSet.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryGetAttributeSet.getNodeClass()),
            },

            {
                name: "Get Attribute Component",
                node: new PointGeometryGetAttributeComponent({
                    parameters: {
                        name: "Get Attribute Component",
                        attribute: null,
                        component: "x",
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryGetAttributeComponent.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryGetAttributeComponent.getNodeClass()),
            },
            {
                name: "Get Attribute Component",
                node: new CurveGeometryGetAttributeComponent({
                    parameters: {
                        name: "Get Attribute Component",
                        attribute: null,
                        component: "x",
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryGetAttributeComponent.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryGetAttributeComponent.getNodeClass()),
            },
            {
                name: "Get Attribute Component",
                node: new MeshGeometryGetAttributeComponent({
                    parameters: {
                        name: "Get Attribute Component",
                        attribute: null,
                        component: "x",
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryGetAttributeComponent.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryGetAttributeComponent.getNodeClass()),
            },
            {
                name: "Pack Operator",
                node: new PointGeometryPackOperator({
                    parameters: {
                        name: "Pack Operator",
                        x: null,
                        y: null,
                        z: null,
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryPackOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryPackOperator.getNodeClass()),
            },
            {
                name: "Pack Operator",
                node: new CurveGeometryPackOperator({
                    parameters: {
                        name: "Pack Operator",
                        x: null,
                        y: null,
                        z: null,
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryPackOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryPackOperator.getNodeClass()),
            },
            {
                name: "Pack Operator",
                node: new MeshGeometryPackOperator({
                    parameters: {
                        name: "Pack Operator",
                        x: null,
                        y: null,
                        z: null,
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryPackOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryPackOperator.getNodeClass()),
            },
            {
                name: "Math Operator",
                node: new PointGeometryMathOperator({
                    parameters: {
                        name: "Math Operator",
                        operator: createPointGeometryUnaryOrBinaryMathOperator("add", null, null),
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryMathOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryMathOperator.getNodeClass()),
            },
            {
                name: "Math Operator",
                node: new CurveGeometryMathOperator({
                    parameters: {
                        name: "Math Operator",
                        operator: createCurveGeometryUnaryOrBinaryMathOperator("add", null, null),
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryMathOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryMathOperator.getNodeClass()),
            },
            {
                name: "Math Operator",
                node: new MeshGeometryMathOperator({
                    parameters: {
                        name: "Math Operator",
                        operator: createMeshGeometryUnaryOrBinaryMathOperator("add", null, null),
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryMathOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryMathOperator.getNodeClass()),
            },
            {
                name: "Join Operator",
                node: new PointGeometryJoinOperator({
                    parameters: {
                        name: "Join Operator",
                        primaryAttributeA: null,
                        attributeSetA: null,
                        primaryAttributeB: null,
                        attributeSetB: null,
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryJoinOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryJoinOperator.getNodeClass()),
            },
            {
                name: "Join Operator",
                node: new CurveGeometryJoinOperator({
                    parameters: {
                        name: "Join Operator",
                        primaryAttributeA: null,
                        attributeSetA: null,
                        primaryAttributeB: null,
                        attributeSetB: null,
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryJoinOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryJoinOperator.getNodeClass()),
            },
            {
                name: "Join Operator",
                node: new MeshGeometryJoinOperator({
                    parameters: {
                        name: "Join Operator",
                        primaryAttributeA: null,
                        attributeSetA: null,
                        primaryAttributeB: null,
                        attributeSetB: null,
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryJoinOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryJoinOperator.getNodeClass()),
            },
            {
                name: "Reduce Operator",
                node: new GeometryReduceOperator({
                    parameters: {
                        name: "Reduce Operator",
                        attribute: null,
                        operator: "min",
                    },
                }),
                iconClass: getNodeIconClass(GeometryReduceOperator.getNodeClass()),
            },
            {
                name: "Flatten Operator",
                node: new CurveGeometryFlattenOperator({
                    parameters: {
                        name: "Flatten Operator",
                        attribute: null,
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryFlattenOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryFlattenOperator.getNodeClass()),
            },
            {
                name: "Const operator",
                node: new PointGeometryConstOperator({
                    parameters: {
                        name: "Const operator",
                        attribute: null,
                        value: null,
                        type: "like",
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryConstOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryConstOperator.getNodeClass()),
            },
            {
                name: "Const operator",
                node: new CurveGeometryConstOperator({
                    parameters: {
                        name: "Const operator",
                        attribute: null,
                        value: null,
                        type: "like",
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryConstOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryConstOperator.getNodeClass()),
            },
            {
                name: "Const operator",
                node: new MeshGeometryConstOperator({
                    parameters: {
                        name: "Const operator",
                        attribute: null,
                        value: null,
                        type: "like",
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryConstOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryConstOperator.getNodeClass()),
            },
            {
                name: "Cast Operator",
                node: new PointGeometryCastOperator({
                    parameters: {
                        name: "Cast Operator",
                        attribute: null,
                        type: "float",
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryCastOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryCastOperator.getNodeClass()),
            },
            {
                name: "Cast Operator",
                node: new CurveGeometryCastOperator({
                    parameters: {
                        name: "Cast Operator",
                        attribute: null,
                        type: "float",
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryCastOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryCastOperator.getNodeClass()),
            },
            {
                name: "Cast Operator",
                node: new MeshGeometryCastOperator({
                    parameters: {
                        name: "Cast Operator",
                        attribute: null,
                        type: "float",
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryCastOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryCastOperator.getNodeClass()),
            },
            {
                name: "Diff Operator",
                node: new CurveGeometryDiffOperator({
                    parameters: {
                        name: "Diff Operator",
                        independentAttribute: null,
                        dependentAttribute: null,
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryDiffOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryDiffOperator.getNodeClass()),
            },
            {
                name: "Primitive",
                node: new PointGeometryPrimitive({
                    parameters: {
                        name: "Primitive",
                        primitive: createPointGeometryPrimitive("linePoints1", {numPoints: 1, x0: null, x1: null}),
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryPrimitive.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryPrimitive.getNodeClass()),
            },
            {
                name: "Primitive",
                node: new CurveGeometryPrimitive({
                    parameters: {
                        name: "Primitive",
                        primitive: createCurveGeometryPrimitive("line1", null, {x0: null, x1: null}),
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryPrimitive.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryPrimitive.getNodeClass()),
            },
            {
                name: "Primitive",
                node: new MeshGeometryPrimitive({
                    parameters: {
                        name: "Primitive",
                        primitive: createMeshGeometryPrimitive("grid2", {
                            originX: null,
                            originY: null,
                            basis1X: null,
                            basis1Y: null,
                            basis2X: null,
                            basis2Y: null,
                            numXSegments: null,
                            numYSegments: null,
                        }),
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryPrimitive.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryPrimitive.getNodeClass()),
            },
            {
                name: "Triangulate Operator",
                node: new MeshGeometryTriangulateOperator({
                    parameters: {
                        name: "Triangulate Operator",
                        primaryAttribute: null,
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryTriangulateOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryTriangulateOperator.getNodeClass()),
            },
            {
                name: "Label primitives operator",
                node: new PointGeometryLabelPrimitivesOperator({
                    parameters: {
                        name: "Label primitives operator",
                        attribute: null,
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryLabelPrimitivesOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryLabelPrimitivesOperator.getNodeClass()),
            },
            {
                name: "Label primitives operator",
                node: new CurveGeometryLabelPrimitivesOperator({
                    parameters: {
                        name: "Label primitives operator",
                        attribute: null,
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryLabelPrimitivesOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryLabelPrimitivesOperator.getNodeClass()),
            },
            {
                name: "Label primitives operator",
                node: new MeshGeometryLabelPrimitivesOperator({
                    parameters: {
                        name: "Label primitives operator",
                        attribute: null,
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryLabelPrimitivesOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryLabelPrimitivesOperator.getNodeClass()),
            },
            {
                name: "Product Operator",
                node: new PointGeometryProductOperator({
                    parameters: {
                        name: "Product Operator",
                        operator: new PointGeometryProductOperatorPointGeometryPointGeometryInput({
                            parameters: {
                                primaryAttributeA: null,
                                attributeSetA: null,
                                primaryAttributeB: null,
                                attributeSetB: null,
                            },
                        }),
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryProductOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryProductOperator.getNodeClass()),
            },
            {
                name: "Product Operator",
                node: new CurveGeometryProductOperator({
                    parameters: {
                        name: "Product Operator",
                        operator: new CurveGeometryProductOperatorPointGeometryCurveGeometryInput({
                            parameters: {
                                primaryAttributeA: null,
                                attributeSetA: null,
                                primaryAttributeB: null,
                                attributeSetB: null,
                            },
                        }),
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryProductOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryProductOperator.getNodeClass()),
            },
            {
                name: "Product Operator",
                node: new MeshGeometryProductOperator({
                    parameters: {
                        name: "Product Operator",
                        operator: new MeshGeometryProductOperatorPointGeometryMeshGeometryInput({
                            parameters: {
                                primaryAttributeA: null,
                                attributeSetA: null,
                                primaryAttributeB: null,
                                attributeSetB: null,
                            },
                        }),
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryProductOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryProductOperator.getNodeClass()),
            },
            {
                name: "Project Operator",
                node: new PointGeometryProjectOperator({
                    parameters: {
                        name: "Project Operator",
                        sourceAttribute: null,
                        targetAttribute: null,
                        targetAttributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryProjectOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryProjectOperator.getNodeClass()),
            },
            {
                name: "Project Operator",
                node: new CurveGeometryProjectOperator({
                    parameters: {
                        name: "Project Operator",
                        sourceAttribute: null,
                        targetAttribute: null,
                        targetAttributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryProjectOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryProjectOperator.getNodeClass()),
            },
            {
                name: "Project Operator",
                node: new MeshGeometryProjectOperator({
                    parameters: {
                        name: "Project Operator",
                        sourceAttribute: null,
                        targetAttribute: null,
                        targetAttributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryProjectOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryProjectOperator.getNodeClass()),
            },
            {
                name: "Cut Operator",
                node: new MeshGeometryCutOperator({
                    parameters: {
                        name: "Cut Operator",
                        meshUVAttribute: null,
                        meshAttributeSet: null,
                        curveUVAttribute: null,
                        curveGroupIDAttribute: null,
                        groupIDs: [],
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryCutOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryCutOperator.getNodeClass()),
            },
            {
                name: "Filter Operator",
                node: new PointGeometryFilterOperator({
                    parameters: {
                        name: "Filter Operator",
                        primaryAttribute: null,
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryFilterOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryFilterOperator.getNodeClass()),
            },
            {
                name: "Filter Operator",
                node: new CurveGeometryFilterOperator({
                    parameters: {
                        name: "Filter Operator",
                        primaryAttribute: null,
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryFilterOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryFilterOperator.getNodeClass()),
            },
            {
                name: "Filter Operator",
                node: new MeshGeometryFilterOperator({
                    parameters: {
                        name: "Filter Operator",
                        primaryAttribute: null,
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryFilterOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryFilterOperator.getNodeClass()),
            },
            {
                name: "Partition Operator",
                node: new PointGeometryPartitionOperator({
                    parameters: {
                        name: "Partition Operator",
                        primaryAttribute: null,
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryPartitionOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryPartitionOperator.getNodeClass()),
            },
            {
                name: "Partition Operator",
                node: new CurveGeometryPartitionOperator({
                    parameters: {
                        name: "Partition Operator",
                        primaryAttribute: null,
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryPartitionOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryPartitionOperator.getNodeClass()),
            },
            {
                name: "Partition Operator",
                node: new MeshGeometryPartitionOperator({
                    parameters: {
                        name: "Partition Operator",
                        primaryAttribute: null,
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryPartitionOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryPartitionOperator.getNodeClass()),
            },
            {
                name: "Flip Operator",
                node: new CurveGeometryFlipOperator({
                    parameters: {
                        name: "Flip Operator",
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryFlipOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryFlipOperator.getNodeClass()),
            },
            {
                name: "Flip Operator",
                node: new MeshGeometryFlipOperator({
                    parameters: {
                        name: "Flip Operator",
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryFlipOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryFlipOperator.getNodeClass()),
            },
            {
                name: "Boundary Operator",
                node: new PointGeometryBoundaryOperator({
                    parameters: {
                        name: "Boundary Operator",
                        primaryAttribute: null,
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryBoundaryOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryBoundaryOperator.getNodeClass()),
            },
            {
                name: "Boundary Operator",
                node: new CurveGeometryBoundaryOperator({
                    parameters: {
                        name: "Boundary Operator",
                        primaryAttribute: null,
                        attributeSet: null,
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryBoundaryOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryBoundaryOperator.getNodeClass()),
            },
            {
                name: "Merge Operator",
                node: new PointGeometryMergeOperator({
                    parameters: {
                        name: "Merge Operator",
                        attributeSets: [],
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryMergeOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryMergeOperator.getNodeClass()),
            },
            {
                name: "Merge Operator",
                node: new CurveGeometryMergeOperator({
                    parameters: {
                        name: "Merge Operator",
                        attributeSets: [],
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryMergeOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryMergeOperator.getNodeClass()),
            },
            {
                name: "Merge Operator",
                node: new MeshGeometryMergeOperator({
                    parameters: {
                        name: "Merge Operator",
                        attributeSets: [],
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryMergeOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryMergeOperator.getNodeClass()),
            },
            {
                name: "Weld Operator",
                node: new PointGeometryWeldOperator({
                    parameters: {
                        name: "Weld Operator",
                        attribute: null,
                        tolerance: 1e-4,
                    },
                }),
                iconClass: getNodeIconClass(PointGeometryWeldOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometryWeldOperator.getNodeClass()),
            },
            {
                name: "Weld Operator",
                node: new CurveGeometryWeldOperator({
                    parameters: {
                        name: "Weld Operator",
                        attribute: null,
                        tolerance: 1e-4,
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometryWeldOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometryWeldOperator.getNodeClass()),
            },
            {
                name: "Weld Operator",
                node: new MeshGeometryWeldOperator({
                    parameters: {
                        name: "Weld Operator",
                        attribute: null,
                        tolerance: 1e-4,
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryWeldOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryWeldOperator.getNodeClass()),
            },
            {
                name: "Split Operator",
                node: new PointGeometrySplitOperator({
                    parameters: {
                        name: "Split Operator",
                        primaryAttribute: null,
                        attributeSet: null,
                        count: null,
                    },
                }),
                iconClass: getNodeIconClass(PointGeometrySplitOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(PointGeometrySplitOperator.getNodeClass()),
            },
            {
                name: "Split Operator",
                node: new CurveGeometrySplitOperator({
                    parameters: {
                        name: "Split Operator",
                        primaryAttribute: null,
                        attributeSet: null,
                        count: null,
                    },
                }),
                iconClass: getNodeIconClass(CurveGeometrySplitOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(CurveGeometrySplitOperator.getNodeClass()),
            },
            {
                name: "Split Operator",
                node: new MeshGeometrySplitOperator({
                    parameters: {
                        name: "Split Operator",
                        primaryAttribute: null,
                        attributeSet: null,
                        count: null,
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometrySplitOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometrySplitOperator.getNodeClass()),
            },
            {
                name: "Normals Operator",
                node: new MeshGeometryNormalsOperator({
                    parameters: {
                        name: "Normals Operator",
                        positionAttribute: null,
                        smoothingAttribute: null,
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometryNormalsOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometryNormalsOperator.getNodeClass()),
            },
            {
                name: "Subdivide Operator",
                node: new MeshGeometrySubdivideOperator({
                    parameters: {
                        name: "Subdivide Operator",
                        subdivisionLevel: null,
                        positionAttribute: null,
                        vertexVaryingAttributes: null,
                        primitiveUniformAttributes: null,
                    },
                }),
                iconClass: getNodeIconClass(MeshGeometrySubdivideOperator.getNodeClass()),
                secondaryIconClass: getNodeIconSecondaryClass(MeshGeometrySubdivideOperator.getNodeClass()),
            },
        ]
    })

    readonly $sceneItems = computed<SceneItem[]>(() => {
        const currentSection = this.$currentSection()
        switch (currentSection) {
            case "upload":
                return this.$uploadSceneItems()
            case "primitive":
                return this.$primitiveSceneItems()
            case "configuration":
                return this.$configurationSceneItems()
            case "decal":
                return this.$decalSceneItems()
            case "input":
                return this.$inputSceneItems()
            case "export":
                return this.$exportSceneItems()
            case "value":
                return this.$valueSceneItems()
            case "connection":
                return this.$connectionSceneItems()
            case "geometry":
                return this.$geometrySceneItems()
            case "other":
                return this.$otherSceneItems()
            case "systemTemplate":
                return []
            case "meshSpecificMaterial":
                return []
            case "materialRange":
                return []
        }
    })

    addItem(node: TemplateNode) {
        this.sceneManagerService.modifyTemplateGraph((graph) => {
            graph.parameters.nodes.addEntry(node)
        })
    }

    dragStart(event: DragEvent, node: TemplateNode) {
        this.drag.dragStart(event, node)
    }

    private async uploadMesh(): Promise<false | void> {
        const file = await selectFile("Select a mesh file for upload", ".cmm,.obj,.ply,.fbx")
        if (!file) return false

        const action = async () => {
            try {
                const group = await uploadMeshToGroup({
                    injector: this.injector,
                    uploadService: this.uploadService,
                    workerService: this.workerService,
                    file,
                    organizationLegacyId: this.sceneManagerService.$defaultCustomerId(),
                    templateRevisionId: this.sceneManagerService.$templateRevisionId(),
                })
                if (!group) return

                this.addItem(group)
            } catch (error) {
                if (error instanceof MeshMissingUvsError || error instanceof MeshConversionError) {
                    this.notifications.showError(error.message, 0, "Close")
                    return
                }
                throw error
            }
        }

        this.sceneManagerService.addTask({
            description: `uploadMesh("${file.name}")`,
            task: from(defer(() => action())),
            critical: true,
        })
    }

    private async uploadImage(): Promise<false | void> {
        const file = await selectFile("Select an image file for upload", ".jpeg,.jpg,.png,.tiff,.exr")
        if (!file) return false

        const action = async () => {
            const image = await uploadImage(this.injector, this.uploadService, this.sceneManagerService, this.dialog, file)
            if (!image) return

            this.addItem(image)
        }

        this.sceneManagerService.addTask({
            description: `uploadImage("${file.name}")`,
            task: from(defer(() => action())),
            critical: true,
        })
    }

    private rigidlyConnectSelection() {
        const selectedObjects = this.$selectedObjects()
        if (selectedObjects.length < 2) return

        const primaryObj = selectedObjects[0]

        const nodeOwner = getNodeOwner(primaryObj)
        if (!nodeOwner) throw new Error("Cannot find node owner for selected object")

        this.sceneManagerService.modifyTemplateGraph(() => {
            const matrixA = this.sceneManagerService.getTransformAccessor(primaryObj)?.getTransform()

            if (!matrixA) return

            for (let i = 1; i < selectedObjects.length; i++) {
                const secondaryObject = selectedObjects[i]
                const matrixB = this.sceneManagerService.getTransformAccessor(secondaryObject)?.getTransform()

                if (!matrixB) continue

                const {position, quaternion} = matrixA.inverse().multiply(matrixB).decompose()
                const angles = quaternionToAngleDegrees(quaternion)

                nodeOwner.parameters.nodes.addEntry(
                    new RigidRelation({
                        parameters: {
                            translation: new Translation({parameters: {x: position.x, y: position.y, z: position.z}}),
                            rotation: new FixedRotation({parameters: {x: angles[0], y: angles[1], z: angles[2]}}),
                            targetA: primaryObj,
                            targetB: secondaryObject,
                        },
                    }),
                )
                secondaryObject.updateParameters({lockedTransform: undefined})
            }
        })
    }

    private addPointGuideForSelection() {
        const selectedPointGuideNodes = this.$selectedPointGuideNodes()

        if (selectedPointGuideNodes.length === 0) return

        const centroidAccumulator = new CentroidAccumulator()
        selectedPointGuideNodes.forEach((sceneNode) => centroidAccumulator.accumulate(sceneNode.completeMeshData.reified, sceneNode.transform))
        const centroid = Vector3.fromArray(centroidAccumulator.finalize().centroid)
        const transform = Matrix4.translation(centroid.x, centroid.y, centroid.z).toArray()

        this.addItem(
            new PointGuide({
                parameters: {
                    name: "Point Guide",
                    lockedTransform: transform,
                    $defaultTransform: transform,
                    visible: true,
                },
            }),
        )
    }
}
